import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';
import { comparisonRows } from '@/mocks/comparison';

interface Cell {
  text: string;
  good: boolean;
}

function ValueCell({ cell, highlight }: { cell: Cell; highlight: boolean }) {
  return (
    <div
      className={`flex items-start gap-2.5 px-4 md:px-5 py-4 ${
        highlight ? 'bg-accent-50' : ''
      }`}
    >
      <span
        className={`shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center rounded-full ${
          cell.good
            ? 'bg-primary-100 text-primary-700'
            : 'bg-background-200 text-secondary-600'
        }`}
      >
        <i className={cell.good ? 'ri-check-line text-sm' : 'ri-close-line text-sm'} aria-hidden="true"></i>
      </span>
      <span
        className={`text-xs md:text-sm leading-relaxed ${
          highlight ? 'font-medium text-foreground-900' : 'text-foreground-600'
        }`}
      >
        {cell.text}
      </span>
    </div>
  );
}

const OPTIONS: { key: 'goldwarden' | 'generic' | 'manual'; label: string }[] = [
  { key: 'goldwarden', label: 'GoldWarden EA' },
  { key: 'generic', label: 'Typical gold EA' },
  { key: 'manual', label: 'Manual trading' },
];

function MobileValueRow({ name, cell, highlight }: { name: string; cell: Cell; highlight: boolean }) {
  return (
    <div
      className={`flex items-start gap-3 px-4 py-3.5 ${
        highlight ? 'bg-accent-50' : ''
      }`}
    >
      <span
        className={`shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center rounded-full ${
          cell.good
            ? 'bg-primary-100 text-primary-700'
            : 'bg-background-200 text-secondary-600'
        }`}
      >
        <i className={cell.good ? 'ri-check-line text-sm' : 'ri-close-line text-sm'} aria-hidden="true"></i>
      </span>
      <div className="min-w-0">
        <p
          className={`text-[11px] font-semibold uppercase tracking-[0.12em] ${
            highlight ? 'text-accent-900' : 'text-foreground-500'
          }`}
        >
          {name}
        </p>
        <p
          className={`mt-0.5 text-xs leading-relaxed ${
            highlight ? 'font-medium text-foreground-900' : 'text-foreground-600'
          }`}
        >
          {cell.text}
        </p>
      </div>
    </div>
  );
}

export default function Comparison() {
  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="Why traders switch"
              title="How GoldWarden compares to the alternatives"
              subtitle="Free is only part of the story. What actually separates a survivable gold EA from a blown account is how it handles risk — here is the honest breakdown."
            />
          </Reveal>

          {/* mobile: stacked cards */}
          <div className="mt-10 flex flex-col gap-3 lg:hidden">
            {comparisonRows.map((row, index) => (
              <Reveal key={row.id} delay={index * 50}>
                <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  <div className="border-b border-background-200 bg-background-100 px-4 py-3">
                    <h3 className="font-heading text-sm font-bold text-foreground-950">{row.label}</h3>
                  </div>
                  <div className="flex flex-col divide-y divide-background-200/70">
                    {OPTIONS.map((option) => (
                      <MobileValueRow
                        key={option.key}
                        name={option.label}
                        cell={row[option.key]}
                        highlight={option.key === 'goldwarden'}
                      />
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* desktop: comparison table */}
          <Reveal delay={80}>
            <div className="mt-12 hidden lg:block">
              <div className="min-w-[760px] rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                {/* header */}
                <div className="grid grid-cols-[1.2fr_1fr_1fr_1fr] border-b border-background-200">
                  <div className="px-4 md:px-5 py-4 flex items-center">
                    <span className="text-xs font-semibold uppercase tracking-[0.14em] text-foreground-500">
                      Feature
                    </span>
                  </div>
                  <div className="px-4 md:px-5 py-4 bg-accent-50 border-l border-background-200 flex items-center gap-2">
                    <span className="w-6 h-6 flex items-center justify-center rounded-md bg-gradient-to-br from-accent-400 to-accent-600 text-primary-950">
                      <i className="ri-shield-check-line text-sm" aria-hidden="true"></i>
                    </span>
                    <span className="font-heading text-sm font-bold text-foreground-950">GoldWarden EA</span>
                  </div>
                  <div className="px-4 md:px-5 py-4 border-l border-background-200 flex items-center">
                    <span className="font-heading text-sm font-semibold text-foreground-700">Typical gold EA</span>
                  </div>
                  <div className="px-4 md:px-5 py-4 border-l border-background-200 flex items-center">
                    <span className="font-heading text-sm font-semibold text-foreground-700">Manual trading</span>
                  </div>
                </div>

                {/* rows */}
                {comparisonRows.map((row) => (
                  <div
                    key={row.id}
                    className="grid grid-cols-[1.2fr_1fr_1fr_1fr] border-b border-background-200/70 last:border-b-0"
                  >
                    <div className="px-4 md:px-5 py-4 flex items-center">
                      <span className="text-xs md:text-sm font-semibold text-foreground-900">{row.label}</span>
                    </div>
                    <div className="bg-accent-50 border-l border-background-200">
                      <ValueCell cell={row.goldwarden} highlight />
                    </div>
                    <div className="border-l border-background-200">
                      <ValueCell cell={row.generic} highlight={false} />
                    </div>
                    <div className="border-l border-background-200">
                      <ValueCell cell={row.manual} highlight={false} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <p className="mt-4 text-xs text-foreground-500 text-center">
            Comparison reflects how risk is typically handled — individual EAs and traders vary. Always test on a demo account first.
          </p>
        </div>
      </div>
    </section>
  );
}